import { config } from '../../../../config';
import DigiobcanApi from '../../../api/DigiobcanApi';
import type { ReportCategory } from '../../../api/DigiobcanApi';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import UserAvatarStack from '../../../components/UserAvatarStack';
import FormAutocompleteField from '../../../components/form/FormAutocompleteField';
import FormTextField from '../../../components/form/FormTextField';
import { getReportCategories } from '../../../features/queries/getReportCategories';
import { useManagedReportCategoryIds } from '../../../features/queries/meGetReportCategories';
import { shortId } from '../../../lib/ids';
import { QueryNameSpace, useM, useQ } from '../../../lib/query';
import { useLayout } from '../../../providers/LayoutProvider';
import { useLogger } from '../../../providers/LoggerProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import { __, required, toastError, toastSuccess } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import { useAuthRoles } from '@presov.sk/presov-auth-react';
import {
  Box,
  Button,
  DataTable,
  EmptyState,
  FilterBar,
  FilterChip,
  MenuItem,
  Radio,
  Select,
  Stack,
  Tooltip,
  Typography,
  useUrlFilters,
  type Column,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Form } from 'react-final-form';
import { useSearchParams } from 'react-router';

/**
 * Riadok tabuľky. Od `ReportCategory` sa líši len tým, že `fullPath` je vždy
 * vyplnená - `full_path` z API je voliteľná (staršie odpovede ju nemajú)
 * a bunka s prázdnym názvom by vyzerala ako chyba.
 */
interface CategoryRow extends ReportCategory {
  fullPath: string;
}

const ReportsCategoriesAdminPage: React.FC = () => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthRoles();
  const { setActions, setFooter, setPageFilters } = useLayout();
  const { isMd } = useResponsive();

  // Hodnota hľadania patrí do URL, aby sa dal odkaz na vyfiltrovaný zoznam
  // poslať. `debounce` drží dotaz na API - filtruje sa na serveri, takže každý
  // znak by inak bol jeden request.
  const [searchParams, setSearchParams] = useSearchParams();
  const { query, setQuery, search } = useUrlFilters({
    searchParams,
    setSearchParams,
    debounce: 200,
  });
  const searchValue = search ?? '';

  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  // Cieľ živých nálezov rušených kategórií; prázdne = predvolená (rozhodne API).
  const [moveTo, setMoveTo] = React.useState('');
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  // Kategória čakajúca na potvrdenie, že sa má stať predvolenou. Drží sa celý
  // riadok, nie len ID: dialóg pomenúva aj tú kategóriu, ktorá príznak stratí.
  const [categoryToSetDefault, setCategoryToSetDefault] =
    React.useState<CategoryRow | null>(null);
  const tableRef = React.useRef<HTMLDivElement>(null);

  const canManage = React.useMemo(() => {
    return hasPermission({
      client_id: config.CLIENT_ID,
      permission: 'pruser:report-categories:manage',
    });
  }, [hasPermission]);

  /**
   * Zoznam filtruje **API**, nie prehliadač. Kým to bolo naopak, ťahal sa celý
   * strom a `matchSorter` ho preosieval nad `fullPath` - hľadať podľa mena
   * zodpovednej osoby sa tak nedalo vôbec, lebo mená klient nedostával.
   * `search` je už odladený (`useUrlFilters` má debounce), takže sa dotaz
   * nepúšťa na každý znak.
   *
   * Odpoveď je plochá a zaradenie nesie `full_path` zo servera - pri hľadaní
   * je výsledok útržok stromu, v ktorom rodič nájdenej kategórie byť nemusí.
   */
  const { data: reportCategories = [], isLoading: isLoadingReportCategories } =
    useQ(getReportCategories, { view: 'flat', q: searchValue || undefined });

  /**
   * Nefiltrovaný zoznam pre veci, ktoré hľadanie zúžiť nesmie: ponuku
   * nadradenej kategórie v dialógu a meno kategórie, ktorá práve drží príznak
   * predvolenej. Obe musia platiť aj vtedy, keď ich hľadanie z tabuľky
   * vyhodilo. Je to ten istý dotaz, aký si berie `useDefaultReportCategoryId`,
   * takže ide z cache a nie po drôte.
   */
  const { data: allCategories = [] } = useQ(getReportCategories, undefined, {
    staleTime: 5 * 60 * 1000,
  });

  const handleConfirm = async (values) => {
    if (!navigator.onLine) {
      toastError(__('Internetové pripojenie zlyhalo.'));
      return;
    }
    try {
      await createReportCategoryMutation.mutateAsync({
        name: values.name,
        description: values.description || null,
        ...(values.parentId && { parent_id: values.parentId }),
      });
      // Zatvorenie dialógu len pri úspešnom vytvorení
      setShowAddDialog(false);
    } catch {
      // Modal ostane otvorený pri chybe, aby mohol admin opraviť údaje
    }
  };

  // Správca kategórie bez `pruser:report-categories:manage` sem chodí len
  // spravovať členov svojich kategórií - ostatné by boli riadky, ktoré
  // neotvorí (detail by ho pustil, ale nič by v ňom nesmel).
  const managedCategoryIds = useManagedReportCategoryIds();

  const rows: CategoryRow[] = React.useMemo(
    () =>
      reportCategories
        .filter(
          (category) =>
            canManage || managedCategoryIds.includes(category.category_id),
        )
        .map((category) => ({
          ...category,
          fullPath: category.full_path ?? category.name,
        })),
    [reportCategories, canManage, managedCategoryIds],
  );

  // Kategória, ktorá príznak práve drží. Berie sa z nefiltrovaného zoznamu -
  // dialóg ju musí vedieť pomenovať aj vtedy, keď ju hľadanie z tabuľky
  // vyhodilo. Môže chýbať: predvolená kategória nemusí byť žiadna (zmazala sa).
  const currentDefault = React.useMemo(
    () => allCategories.find((category) => category.is_default),
    [allCategories],
  );

  const createReportCategoryMutation = useM(
    // `category_id` sa neposiela - skladá ho server. Kým to robil klient
    // (slug z názvu), držal kľúč názov kategórie a pri premenovaní začal
    // klamať; dve rovnako pomenované kategórie pod rôznymi rodičmi si navyše
    // vyrobili kolíziu na primárnom kľúči.
    (values: { name: string; description?: string; parent_id?: string }) =>
      DigiobcanApi.createReportCategory(undefined, values),
    {
      onSuccess: () => {
        toastSuccess(__('Kategória bola vytvorená'));

        // Nová kategória sa do cache nedopisuje ručne: odpoveď `POST`-u nenesie
        // `full_path`, tú skladá server až v zozname, takže by riadok pribudol
        // bez cesty. Zoznam je krátky, načíta sa znova.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.getReportCategories],
        });
        // Scrollovací obal vlastní shell, takže sa naň nedá držať ref -
        // `scrollIntoView` si svojho scrollera nájde sám.
        setTimeout(() => {
          tableRef.current
            ?.querySelector('[role="row"]:last-of-type')
            ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }, 100);
      },
      onError: (e, variables) => {
        console.error('Error creating report category', e);
        logger.error('Error creating report category', {
          action: 'createReportCategory',
          error: e,
          data: {
            ...variables,
          },
        });
        if (e?.details?.statusCode === 400) {
          toastError(
            __(
              'Nepodarilo sa vytvoriť kategóriu. Skontrolujte vyplnené údaje.',
            ),
          );
        } else {
          toastError(__('Niečo sa pokazilo'));
        }
      },
    },
  );

  const setDefaultCategoryMutation = useM(
    (categoryId: string) => DigiobcanApi.setDefaultReportCategory(categoryId),
    {
      onSuccess: (res) => {
        toastSuccess(
          res.previous
            ? `${__('Predvolenou kategóriou je')} ${res.category.name}. ${res.previous.name} ${__('príznak stratila.')}`
            : `${__('Predvolenou kategóriou je')} ${res.category.name}.`,
        );
        // Príznak sa presunul medzi dvoma riadkami, takže sa nedá dopísať
        // do cache bodovo. Invaliduje sa celý namespace, nie jeden kľúč: ten
        // nesie aj `q`, takže by sa obnovil len práve vyfiltrovaný zoznam
        // a nefiltrovaný (z ktorého sa číta `currentDefault`) by ostal starý.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.getReportCategories],
        });
      },
      onError: (e, variables) => {
        logger.error('Error setting default report category', {
          action: 'setDefaultReportCategory',
          error: e,
          data: { categoryId: variables },
        });
        toastError(__('Predvolenú kategóriu sa nepodarilo zmeniť.'));
      },
    },
  );

  /**
   * Kategória sa **ruší, nie maže** (API, migrácia 000035): zmizne z ponúk aj
   * zo správy, uzavreté nálezy v nej zostanú ako história a živé presunie API
   * do `moveTo` ako nové podané. Kým sa mazala naozaj, kategória s jediným
   * nálezom sa odstrániť nedala vôbec.
   *
   * Po jednej, nie `Promise.all`: dve rušenia naraz by sa mohli stretnúť na
   * tom istom podnete. Podkategória zrušená s rodičom vráti `categories: 0`,
   * to nie je chyba.
   */
  const deleteReportCategoryMutation = useM(
    async ({ ids, target }: { ids: string[]; target?: string }) => {
      let categories = 0;
      let moved = 0;
      for (const id of ids) {
        const res = await DigiobcanApi.deleteReportCategory(
          id,
          target ? { moveTo: target } : {},
        );
        categories += res.categories;
        moved += res.moved;
      }
      return { categories, moved };
    },
    {
      onSuccess: ({ categories, moved }) => {
        toastSuccess(
          [
            categories === 1
              ? __('Kategória bola zrušená.')
              : `${__('Zrušené kategórie:')} ${categories}.`,
            moved ? `${__('Presunuté živé nálezy:')} ${moved}.` : '',
          ]
            .filter(Boolean)
            .join(' '),
        );

        // Rovnako ako pri prehodení predvolenej: kľúč zoznamu nesie `q`, teda
        // len práve vyfiltrovaný výsledok - zrušené kategórie treba dostať zo
        // všetkých. Ponuky kategórií v zozname podnetov stoja na
        // `meGetReportCategories`.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.getReportCategories],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.meGetReportCategories],
        });
      },
      onError: (e, variables) => {
        logger.error('Error deleting report categories', {
          action: 'deleteReportCategories',
          error: e,
          data: variables,
        });
        // Pri viacerých kategóriách sa zastaví na prvej chybe; tie pred ňou
        // sú zrušené, a zoznam sa preto obnoví aj tu.
        queryClient.invalidateQueries({
          queryKey: [QueryNameSpace.getReportCategories],
        });
        toastError(
          e?.details?.name === 'CATEGORY_IS_DEFAULT'
            ? __(
                'Predvolenú kategóriu nemožno zrušiť. Najprv prehláste za predvolenú inú.',
              )
            : e?.details?.name === 'INVALID_MOVE_TARGET'
              ? __('Do vybranej kategórie sa nálezy presunúť nedajú.')
              : e?.details?.name === 'CATEGORY_RETIRE_CONFLICT'
                ? __(
                    'Niektorý podnet má v kategórii aj v cieľovej kategórii nález, ktorý treba vyriešiť ručne. Kategória sa nezrušila.',
                  )
                : __('Kategóriu sa nepodarilo zrušiť.'),
        );
      },
    },
  );

  /**
   * Rušené kategórie aj s podkategóriami - tie API zruší s rodičom. Z nich sa
   * počíta ponuka cieľa (živé nálezy nemôžu ísť do kategórie, ktorá končí)
   * a či je medzi nimi predvolená, ktorú zrušiť nemožno.
   */
  const retiredSubtree = React.useMemo(() => {
    const ids = new Set(selectedRows);
    let grew = true;
    while (grew) {
      grew = false;
      for (const category of allCategories) {
        if (
          category.parent_id &&
          ids.has(category.parent_id) &&
          !ids.has(category.category_id)
        ) {
          ids.add(category.category_id);
          grew = true;
        }
      }
    }
    return ids;
  }, [selectedRows, allCategories]);
  const retiresDefault =
    !!currentDefault && retiredSubtree.has(currentDefault.category_id);
  const moveTargets = React.useMemo(
    () =>
      allCategories
        .filter((category) => !retiredSubtree.has(category.category_id))
        .map((category) => ({
          value: category.category_id,
          label: category.full_path ?? category.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'sk')),
    [allCategories, retiredSubtree],
  );

  const columns: Column<CategoryRow>[] = React.useMemo(
    () => [
      {
        key: 'category_id',
        label: __('ID'),
        // Odkedy migrácia 000018 zjednotila kľúče, má aj kategória generovaný
        // sedemznakový identifikátor ako podnety a hlasovania - stĺpec je
        // preto rovnako široký a pevný ako tam. Kým tu stál čitateľný slug
        // (`cat:OstatneANezaradenie`), musel byť pružný a bral šírku zvyšku
        // riadku.
        width: '92px',
        minWidth: '92px',
        mono: true,
        render: (category) => shortId(category.category_id),
      },
      {
        key: 'fullPath',
        label: __('Názov'),
        width: '2.6fr',
        minWidth: '220px',
        strong: true,
        // Popis stojí pod názvom, nie za ikonou `i`. Ikona ho schovávala za
        // ďalší pohyb myšou a na dotyku sa k nemu nedalo dostať vôbec - pritom
        // je to práve to, podľa čoho úradník pozná, čo do kategórie patrí.
        render: (category) => (
          <Stack gap={0.15} sx={{ minWidth: 0 }}>
            <Typography variant='body2'>{category.fullPath}</Typography>
            {category.description ? (
              <Typography
                variant='caption'
                sx={{
                  color: 'text.secondary',
                  // Dva riadky a dosť; popis má 500 znakov a riadok tabuľky
                  // sa kvôli nemu nemá natiahnuť cez pol obrazovky.
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {category.description}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        key: 'users',
        label: __('Priradené osoby'),
        width: '1fr',
        minWidth: '140px',
        render: (category) =>
          category.users?.length ? (
            <UserAvatarStack users={category.users} />
          ) : null,
      },
      {
        key: 'is_default',
        label: __('Predvolená'),
        width: '110px',
        minWidth: '110px',
        render: (category) => (
          <Tooltip
            arrow
            title={
              category.is_default
                ? __('Sem padajú podnety, ktoré sa nepodarilo zaradiť')
                : canManage
                  ? __('Prehlásiť túto kategóriu za predvolenú')
                  : ''
            }
          >
            {/* `span` okolo prepínača nie je obal navyše: `Tooltip` počúva
                udalosti na svojom dieťati a **vypnutý prvok žiadne nevydá**,
                takže bez neho by sa nad zamknutým prepínačom (účet bez práva
                na správu kategórií) vysvetlenie nikdy neukázalo. */}
            <span style={{ display: 'inline-flex' }}>
              {/* Radio, nie switch: príznak sa nedá vypnúť, len presunúť inam.
                  Vypnutý stav by sľuboval akciu, ktorú API neponúka - stav bez
                  predvolenej kategórie vzniká len zmazaním, nie prepnutím.

                  Predvolená kategória sa **nezakazuje cez `disabled`**, hoci
                  prepnúť ju na seba samu nejde. Vypnuté `Radio` kreslí MUI
                  sivo bez ohľadu na `color`, a keďže zaškrtnutý je práve vždy
                  ten jeden riadok, bol by farebný prepínač v tabuľke nikdy
                  nevidieť. Netreba to: kliknutie na už zaškrtnutý radio button
                  `change` nevyvolá, takže je to aj tak prázdna operácia. */}
              <Radio
                size='small'
                color='primary'
                checked={!!category.is_default}
                disabled={!canManage}
                onClick={(e) => e.stopPropagation()}
                onChange={() => setCategoryToSetDefault(category)}
                inputProps={{ 'aria-label': __('Predvolená kategória') }}
              />
            </span>
          </Tooltip>
        ),
      },
    ],
    [canManage],
  );

  React.useEffect(() => {
    if (!canManage) {
      setActions([]);
      return;
    }

    // Vytvorenie je akcia stránky, nie ikona v hlavičke tabuľky. Mazanie
    // vybraných patrí k výberu, takže je v pätičke pri jeho počte.
    setActions([
      {
        label: __('Pridať kategóriu'),
        onClick: () => setShowAddDialog(true),
        variant: 'dark',
        icon: <AddIcon />,
      },
    ]);
  }, [canManage, setActions]);

  React.useEffect(() => {
    if (!selectedRows.length) {
      setFooter(null);
      return;
    }

    setFooter(
      <Stack direction='row' alignItems='center' gap={1}>
        <Typography
          variant='body2'
          sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}
        >
          {__('Vybrané:')} {selectedRows.length}
        </Typography>
        <Button
          size='small'
          variant='danger'
          onClick={() => setShowDeleteDialog(true)}
        >
          {/* Vedľa „Zrušiť výber" - preto s predmetom, holé „Zrušiť" by
              znelo ako to druhé. */}
          {selectedRows.length === 1
            ? __('Zrušiť kategóriu')
            : __('Zrušiť kategórie')}
        </Button>
        <Button size='small' variant='text' onClick={() => setSelectedRows([])}>
          {__('Zrušiť výber')}
        </Button>
      </Stack>,
    );

    return () => setFooter(null);
  }, [selectedRows, setFooter]);

  /**
   * Hľadanie patrí do **hlavičky stránky** (slot `page.filters`), nie do tela.
   * Hlavička je pri scrollovaní pripnutá, takže lište neujde pod okraj -
   * kým stála v tele, odscrollovala sa preč a pri dlhom zozname sa k nej
   * dalo vrátiť len scrollom hore. Je to to isté miesto, kde ju má správa
   * podnetov.
   */
  React.useEffect(() => {
    setPageFilters(
      <FilterBar
        variant='header'
        fields={[]}
        value={{}}
        query={query}
        onQueryChange={setQuery}
        searchPlaceholder={__('Hľadať podľa názvu, ID alebo zodpovednej osoby')}
      >
        {searchValue ? (
          <FilterChip
            label={__('Hľadaný výraz')}
            value={searchValue}
            onRemove={() => setQuery('')}
          />
        ) : null}
      </FilterBar>,
    );

    return () => setPageFilters(null);
  }, [setPageFilters, query, setQuery, searchValue]);

  return (
    <>
      {/* Plátno kreslí shell, tu ostáva len odsadenie tabuľky od okrajov. */}
      <Box sx={{ padding: 1.5 }} ref={tableRef}>
        <DataTable
          variant={isMd ? 'table' : 'cards'}
          columns={columns}
          rows={rows}
          rowKey='category_id'
          // Riadok vedie na detail kategórie - tam sa upravuje aj priraďujú
          // zodpovedné osoby. `rowHref` (nie `onRowClick`) preto, aby sa dal
          // detail otvoriť aj cmd-klikom do novej karty.
          rowHref={(category) =>
            `/administracia/kategorie-podnetov/${shortId(category.category_id)}`
          }
          selected={canManage ? selectedRows : undefined}
          onSelectionChange={
            canManage ? (next) => setSelectedRows(next.map(String)) : undefined
          }
          selectAllLabel={__('Označiť všetky kategórie')}
          selectRowLabel={__('Označiť kategóriu')}
          loading={isLoadingReportCategories}
          empty={
            <EmptyState
              compact
              icon={searchValue ? 'search' : 'list'}
              title={
                searchValue
                  ? __('Žiadna kategória nesedí na hľadanie')
                  : __('Žiadne kategórie')
              }
              description={
                searchValue
                  ? __('Skúste iný výraz.')
                  : __('Kategóriu pridáš tlačidlom v hlavičke.')
              }
              actions={
                searchValue ? (
                  <Button
                    variant='outline'
                    size='small'
                    onClick={() => setQuery('')}
                  >
                    {__('Zrušiť hľadanie')}
                  </Button>
                ) : undefined
              }
            />
          }
        />
      </Box>

      <ConfirmationDialog
        open={showDeleteDialog}
        title={
          selectedRows.length === 1
            ? __('Zrušiť kategóriu?')
            : `${__('Zrušiť vybrané kategórie')} (${selectedRows.length})?`
        }
        description={__(
          'Kategória zmizne z ponúk a zo správy kategórií aj so svojimi podkategóriami. Vyriešené a uzavreté nálezy v nej zostanú v histórii podnetov. Živé nálezy (Podané, V riešení) sa presunú do kategórie nižšie ako nové podané - posúdi ich nanovo.',
        )}
        confirmLabel={__('Zrušiť kategóriu')}
        confirmDisabled={retiresDefault}
        onClose={() => {
          setShowDeleteDialog(false);
          setMoveTo('');
        }}
        onConfirm={async () => {
          if (!navigator.onLine) {
            toastError(__('Internetové pripojenie zlyhalo.'));
            return;
          }
          try {
            await deleteReportCategoryMutation.mutateAsync({
              ids: selectedRows,
              target: moveTo || undefined,
            });
            setSelectedRows([]);
          } catch {
            // Empty catch because mutation handles errors
          }
          setShowDeleteDialog(false);
          setMoveTo('');
        }}
        isLoading={deleteReportCategoryMutation.isPending}
        fullWidth
        maxWidth='sm'
      >
        {retiresDefault ? (
          <Typography variant='body2' color='error'>
            {`${__('Medzi vybranými je predvolená kategória')} „${currentDefault?.full_path ?? currentDefault?.name}". ${__('Tú zrušiť nemožno - najprv prehláste za predvolenú inú.')}`}
          </Typography>
        ) : (
          <Stack gap={0.75}>
            <Typography variant='body2' color='text.secondary'>
              {__('Živé nálezy presunúť do')}
            </Typography>
            <Select
              size='small'
              fullWidth
              displayEmpty
              value={moveTo}
              onChange={(e) => setMoveTo(String(e.target.value))}
              renderValue={(value) =>
                value
                  ? (moveTargets.find((t) => t.value === value)?.label ?? '')
                  : `${currentDefault?.full_path ?? currentDefault?.name ?? __('predvolenej kategórie')} (${__('predvolená')})`
              }
            >
              <MenuItem value=''>
                {`${currentDefault?.full_path ?? currentDefault?.name ?? __('predvolenej kategórie')} (${__('predvolená')})`}
              </MenuItem>
              {moveTargets
                .filter((t) => t.value !== currentDefault?.category_id)
                .map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
            </Select>
          </Stack>
        )}
      </ConfirmationDialog>

      <ConfirmationDialog
        open={showAddDialog}
        confirmLabel={__('Vytvoriť')}
        title={__('Vytvoriť novú kategóriu')}
        formId='createForm'
        onClose={() => {
          setShowAddDialog(false);
        }}
        isLoading={createReportCategoryMutation.isPending}
        fullWidth
        maxWidth='xs'
      >
        <Form
          onSubmit={handleConfirm}
          initialValues={{ name: '', description: '' }}
        >
          {({ handleSubmit }) => (
            <form
              id='createForm'
              onSubmit={handleSubmit}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <FormTextField
                required
                label={__('Názov kategórie')}
                name='name'
                validate={required}
                maxLength={120}
              />
              <FormTextField
                required
                label={__('Popis kategórie')}
                name='description'
                multiline
                validate={required}
                rows={2}
                maxLength={500}
              />
              <FormAutocompleteField
                name='parentId'
                label='Nadradená kategória'
                options={allCategories.map((category) => ({
                  label: category.name,
                  value: category.category_id,
                }))}
              />
            </form>
          )}
        </Form>
      </ConfirmationDialog>

      {/* Prehodenie predvolenej kategórie sa potvrdzuje, lebo to nie je zmena
          jedného riadku: príznak zároveň stráca kategória, ktorá ho mala,
          a od nej sa odvíja, kam padnú nezaradené podnety. Dialóg preto
          menuje obe. */}
      <ConfirmationDialog
        open={!!categoryToSetDefault}
        title={__('Prehlásiť za predvolenú kategóriu?')}
        confirmLabel={__('Prehlásiť')}
        description={
          categoryToSetDefault
            ? [
                `${__('Podnety, ktoré sa nepodarí zaradiť, budú odteraz padať do kategórie')} „${categoryToSetDefault.fullPath}".`,
                currentDefault
                  ? `${__('Kategória')} „${currentDefault.full_path ?? currentDefault.name}" ${__('tento príznak stratí - predvolená môže byť len jedna.')}`
                  : __(
                      'Predvolenú kategóriu zatiaľ nemá nastavenú žiadna iná.',
                    ),
              ].join(' ')
            : ''
        }
        onClose={() => setCategoryToSetDefault(null)}
        onConfirm={async () => {
          if (!navigator.onLine) {
            toastError(__('Internetové pripojenie zlyhalo.'));
            return;
          }
          try {
            await setDefaultCategoryMutation.mutateAsync(
              categoryToSetDefault.category_id,
            );
          } catch {
            // Empty catch because mutation handles errors
          }
          setCategoryToSetDefault(null);
        }}
        isLoading={setDefaultCategoryMutation.isPending}
        fullWidth
        maxWidth='xs'
      />
    </>
  );
};

export default ReportsCategoriesAdminPage;
